// 月曆頁的圖例（W58.2 引入 · W58.3 重寫 · W58.4 擴充 · **W58.6 重排**）。
//
// **它說明的是整張月曆格，不只是週期。**
//
// 月曆格上有兩種東西：
//
//   1. **事件** —— chip（紀念日 / 心願 / 日記 / 休假 / 行程 / 手動）
//   2. **週期狀態** —— 底色 + 一朵花（實際 / 預測 / 排卵日 / 排卵期 / 易孕期）
//
// 少了第 1 類，圖例就沒有回答「那個愛心是什麼」—— 而那是使用者第一次看這張
// 圖最可能問的問題（除非他正好知道那是紀念日）。
//
// ---
//
// ## W58.6 的兩條規則
//
// ### 一、**完整**清單，不是「這個月出現了什麼」
//
// W58.3 立過「只列該月真的出現的狀態」，理由是舊圖例寫死四項、與資料無關。
// **這一輪把它改成完整清單**（ADR-0214）：一個**每月形狀都不一樣**的圖例，
// 使用者得每月重新學一次 —— 這本身就是另一種「圖例跟現時不齊」。
//
// **清單是從代碼庫探索出來的，不是猜的**（spec §3.1）：日曆只有 4 個資料來源
// （`CalendarEvent` + `anniversary` / `wishlist` / `diary` 三個 projection），
// 共 7 個分類。其中 **`cycle` 不在事件行裡** —— 後端 `build_day_cells` 把它
// 排除出 chip 清單，它在格子上是**角標 + 底色**，由第二行負責。
//
// `album` 只是 `source_type`（手動事件關聯到相簿），**不是**日曆分類。
//
// ### 二、**所有指示器一律 14×14**
//
// 舊版有**三種尺寸**：週期色塊 22×22、事件圖示盒 `--chip-h`(18px)、歸屬框
// 16px —— 三種尺寸並排，看起來像三套不同的語言。W58.6 全部收成 `w-3.5 h-3.5`。
//
// **icon 與實際一致。** 週期用**同一個 `CycleBadge` 元件**（與格子上那朵花
// 同一顆、同一個顏色通道）；事件用 `CATEGORY_ICON` 的同一顆圖；歸屬用
// `chipClasses()`（**填色 = 你、外框 = TA**，DESIGN §3.1）。兩份近似實作終究
// 會漂移，所以這裡不另畫。

import { CycleBadge } from "@/components/calendar/CycleCellVisuals";
import { cycleWashForKind } from "@/components/calendar/cycleState";
import { CATEGORY_ICON, CATEGORY_LABEL, FILTER_CATEGORY_ORDER, chipClasses } from "@/components/calendar/calendarIcons";
import { cn } from "@/lib/utils/cn";
import type { CalendarCategory } from "@/lib/api/calendar";
import type { CycleVisualKind } from "@/components/calendar/cycleState";

interface CycleEntry {
  kind: CycleVisualKind;
  label: string;
}

/**
 * 順序 = 使用者讀的順序：經期 → 預測 → 排卵日 → 排卵期 → 易孕期。
 *
 * 經期與預測是「關於經期」的一組（玫瑰），排卵日與排卵期是「關於受孕」的一組
 * （紫），易孕期是受孕窗口的前半段。
 */
const CYCLE_ENTRIES: readonly CycleEntry[] = [
  { kind: "actual", label: "經期" },
  { kind: "predicted", label: "預測經期" },
  { kind: "ovulation-day", label: "排卵日" },
  { kind: "ovulation", label: "排卵期" },
  { kind: "fertile", label: "易孕期" },
];

/**
 * 事件類別的圖例順序 —— **篩選面板的清單扣掉 `cycle`**。
 *
 * `FILTER_CATEGORY_ORDER` 是類別詞彙的**單一真相**（圖示、標籤、順序都在
 * `calendarIcons.ts`）。這裡以前自己寫了一份六個類別的陣列 —— 而「哪六個」
 * 的答案其實是「七個扣掉 `cycle`」，所以那份拷貝可以**推導**出來，不必再寫。
 *
 * `cycle` 不在事件行裡，是因為**後端把它排除出 chip 清單**
 * （`build_day_cells(exclude_categories=("cycle",))`）：它在格子上是**角標 +
 * 底色**，由第二行負責。把它放進第一行等於教使用者去找一個不存在的东西。
 */
const EVENT_CATEGORIES: readonly CalendarCategory[] = FILTER_CATEGORY_ORDER.filter(
  (category) => category !== "cycle",
);

/**
 * **所有指示器的尺寸。** 14×14 —— 色塊、圓形圖示、歸屬框一律這個。
 *
 * 圖示本身（事件類別的線圖、週期的花）是**盒子裡的內容**，不是盒子的大小；
 * 盒子是 14×14，圖示是 10px / 9px。
 */
const INDICATOR = "h-3.5 w-3.5";

export function MonthLegend() {
  return (
    <div
      className="flex flex-col gap-s-2 px-s-1 pt-s-3 text-meta text-ink-3"
      data-testid="calendar-month-legend"
    >
      {/* 行 1 —— 事件與模組。 */}
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-s-2"
        data-testid="calendar-month-legend-events"
      >
        {EVENT_CATEGORIES.map((category) => {
          const Icon = CATEGORY_ICON[category];
          return (
            <span
              key={category}
              className="inline-flex items-center gap-1.5"
              data-testid={`calendar-month-legend-category-${category}`}
            >
              {/*
                **中性底色的盒子。** 格子上的 chip 是「填色（你）/ 外框（TA）」，
                而歸屬是**每一格自己**的事 —— 同一個類別可能兩個人都有。所以
                類別這裡用中性盒子（**圖示**才是重點），歸屬另外用兩個**真的
                chip 盒子**示範。
              */}
              <span
                className={cn(
                  "flex shrink-0 items-center justify-center rounded-[4px] bg-paper-deep text-ink-2",
                  INDICATOR,
                )}
                aria-hidden="true"
                data-testid={`calendar-legend-indicator-${category}`}
              >
                <Icon size={10} strokeWidth={2} />
              </span>
              {CATEGORY_LABEL[category]}
            </span>
          );
        })}

        {/*
          **歸屬：填色 = 你、外框 = TA。**

          用的是 `chipClasses()` —— **格子上的那一套**，不是另畫一個圖例色。
          這兩個盒子沒有圖示，因為它們示範的是**盒子**（填 / 空框），而
          圖示講的是類別。
        */}
        <span
          className="inline-flex items-center gap-1.5"
          data-testid="calendar-month-legend-owner"
        >
          <span
            className={cn("shrink-0 rounded-[4px]", INDICATOR, chipClasses("me"))}
            aria-hidden="true"
            data-testid="calendar-legend-indicator-owner-me"
          />
          你
        </span>
        <span
          className="inline-flex items-center gap-1.5"
          data-testid="calendar-month-legend-owner-partner"
        >
          <span
            className={cn("shrink-0 rounded-[4px]", INDICATOR, chipClasses("partner"))}
            aria-hidden="true"
            data-testid="calendar-legend-indicator-owner-partner"
          />
          TA
        </span>
      </div>

      {/* 行 2 —— 生理週期狀態。 */}
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-s-2"
        data-testid="calendar-month-legend-cycle"
      >
        {CYCLE_ENTRIES.map((entry) => (
          <span
            key={entry.kind}
            className="inline-flex items-center gap-1.5"
            data-testid={`calendar-month-legend-cycle-${entry.kind}`}
          >
            {/* 迷你格子 —— 與月曆格同一套視覺語言：**底色 + 花**。

                底色由 `cycleWashForKind()` 算出來 —— **與月曆格同一個函式**
                （不是各寫一份 switch），所以色塊與格子的底色不可能對不上。

                花是**同一個 `CycleBadge` 元件** —— 圖例的花與格子的花是同一朵，
                而且顏色通道也相同（實心 = 事實，空心 = 推算 / 區間）。

                `易孕期` 在格子上**沒有角標**（只有底色），所以圖例這一項也沒有花
                —— `CycleBadge` 對 `fertile` 回傳 `null`。 */}
            <span
              className={cn(
                "flex shrink-0 items-center justify-center rounded-[4px]",
                INDICATOR,
                cycleWashForKind(entry.kind),
              )}
              aria-hidden="true"
              data-testid={`calendar-cycle-legend-swatch-${entry.kind}`}
            >
              <CycleBadge kind={entry.kind} size={9} />
            </span>
            {entry.label}
          </span>
        ))}
      </div>
    </div>
  );
}
